import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, CircleCheckBig } from 'lucide-react';
import { SerialEntryPrompt } from './SerialEntryNudge';
import { computeProbeSessionTotals, type ProbeIngestRow } from './ProbeIngestFeed';

/**
 * SIL-1195. Step 4 of the TrueRead Add Asset flow - shown once the user
 * chooses "Done capturing" (never automatically, and never on the first
 * reading: the probe is reusable across many machines, so step 3 is a dwell
 * state, not a one-shot capture - see ProbeIngestFeed/addAssetWizardSteps).
 *
 * Restates the UX-01 success treatment (SIL-1183's AddAssetSuccessPanel:
 * success mark, centered card, heading takes focus) for a whole session's
 * worth of readings instead of one asset, with a totals breakdown by
 * outcome instead of a single item label.
 *
 * Individual and business orgs render the exact same component - this is
 * what "the business ProbeSuccessCta path is updated to match" means: the
 * old business-only inline CTA (ProbeSuccessCta, shown on the first match)
 * is retired in favor of this, gated the same way for both org types.
 *
 * SIL-1812: profile completion is deferred to the Exchange, so "View in
 * portfolio" is always the one action here.
 */
export interface ProbeSessionSummaryProps {
  rows: ProbeIngestRow[];
  /** SIL-1211: the Confirm view's way back to the run step while the probe's
   * token is still active (finishing a session never revokes it) - omitted
   * (no button rendered) by any caller with nothing live to go back to. */
  onBackToCapture?: () => void;
}

export function ProbeSessionSummary({ rows, onBackToCapture }: ProbeSessionSummaryProps) {
  const { total, addedToAssets, inCustoms, alreadyInAssets, rejected } = computeProbeSessionTotals(rows);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Focus moves to the summary heading on every mount - this component is
  // only ever mounted for the duration of the session summary, so "on
  // mount" is exactly "when the summary appears" (mirrors AddAssetSuccessPanel).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto flex max-w-2xl flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center shadow-sm"
    >
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 animate-[success-mark-scale-in_400ms_ease-out] motion-reduce:animate-none"
      >
        <CircleCheckBig className="h-9 w-9" />
      </span>

      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-xl font-bold text-foreground focus:outline-none"
      >
        {total === 1 ? '1 machine captured.' : `${total} machines captured.`}
      </h2>

      {onBackToCapture && (
        <button
          type="button"
          onClick={onBackToCapture}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
        >
          <ArrowLeft className="h-3 w-3" /> Back to capture session
        </button>
      )}

      {/* SIL-1206: five tiles, always in this order, and Total always equals
          the sum of the other four - every IngestOutcome status lands in
          exactly one (see computeProbeSessionTotals' doc comment). Shown
          unconditionally (even at 0) so the reconciliation is visible at a
          glance; only the paragraph below is conditional on which buckets
          are actually non-empty. */}
      <dl className="grid w-full grid-cols-2 gap-2.5 text-center sm:grid-cols-5">
        <SummaryStat label="Total captured" value={total} />
        <SummaryStat label="Added to Assets" value={addedToAssets} />
        <SummaryStat label="In Customs" value={inCustoms} />
        <SummaryStat label="Already in your assets" value={alreadyInAssets} />
        <SummaryStat label="Rejected" value={rejected} />
      </dl>

      {/* SIL-1206: describes only the outcomes that actually happened this
          session - a zero-Added session no longer claims verification is
          running for "everything added automatically" when nothing was. */}
      <p className="max-w-md text-sm text-muted-foreground">
        {[
          addedToAssets > 0 &&
            `Verification is running for the ${addedToAssets === 1 ? 'machine' : `${addedToAssets} machines`} added automatically. Your SX Report and Silicon Passport will be ready shortly for each.`,
          inCustoms > 0 &&
            `${inCustoms === 1 ? '1 machine needs' : `${inCustoms} machines need`} manual review in Customs before it can become an asset.`,
          alreadyInAssets > 0 &&
            `${alreadyInAssets === 1 ? '1 reading matches' : `${alreadyInAssets} readings match`} hardware already in your assets and needed no changes.`,
          rejected > 0 &&
            `${rejected === 1 ? '1 reading was' : `${rejected} readings were`} rejected and recorded nothing.`,
        ]
          .filter(Boolean)
          .join(' ')}
      </p>

      <Link
        to="/assets"
        className="mt-1 inline-flex h-11 w-full max-w-xs items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
      >
        View in portfolio <ArrowRight className="h-4 w-4" />
      </Link>

      <SerialEntryPrompt />
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-num mt-1 text-2xl font-bold text-foreground">{value}</dd>
    </div>
  );
}
